import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {DatePipe} from '@angular/common';
import {Observable, Observer} from 'rxjs';
import {UploadFile as NzUploadFile} from 'ng-zorro-antd/upload';
import {EnumSignMethod} from 'src/app/core/constants/contract';
import {CanvasService} from 'src/app/core/services/canvas.service';
import {ImageGenerateService} from 'src/app/core/services/image-generate.service';
import {CommonService} from 'src/app/core/services/common.service';
import {SignDocumentService} from 'src/app/core/services/sign-document.service';
import {SignatureConfig} from '../../document/sign-flow-document/signature-config.model';

/**
 * Loại ảnh ký mặc định của phương thức ký (PYC-84222 task 3).
 */
export enum DefaultSignImageType {
  SYSTEM = 0,
  UPLOAD = 1,
  DRAW = 2
}

/**
 * Chuẩn hoá chuỗi defaultSignConfig để so sánh "người dùng có sửa config hay không":
 * bỏ 2 field kỹ thuật BE gắn thêm vào JSON trả về (signatureImageBase64 - base64 ảnh nhúng
 * đã inject, signatureImagePath - path storage) và sort key, để chuỗi sinh từ nguồn khác
 * nhau (compact FE vs JSON enrich từ BE) vẫn so sánh bằng được khi nội dung không đổi.
 */
export function normalizeSignConfigForCompare(json: string): string {
  if (!json) {
    return '';
  }
  try {
    const parsed = JSON.parse(json);
    delete parsed.signatureImageBase64;
    delete parsed.signatureImagePath;
    const sorted: any = {};
    Object.keys(parsed).sort().forEach(key => {
      sorted[key] = parsed[key];
    });
    return JSON.stringify(sorted);
  } catch (e) {
    return json;
  }
}

/**
 * Block "Chọn ảnh chữ ký" trong Thêm mới/Chi tiết phương thức ký:
 * 3 tab Ảnh mặc định hệ thống / Ảnh ký / Vẽ chữ ký.
 * Chuyển tab không mất dữ liệu tab kia; component cha đọc state qua các Input để lưu.
 */
@Component({
  selector: 'app-default-sign-image-config',
  templateUrl: './default-sign-image-config.component.html',
  styleUrls: ['./default-sign-image-config.component.scss']
})
export class DefaultSignImageConfigComponent implements OnChanges {

  // kích thước khung vẽ chữ ký trong dialog cấu hình ảnh ký mặc định
  canvasWidth = 450;
  canvasHeight = Math.round(450 * (136 / 300));;

  constructor(private sanitizer: DomSanitizer,
              private canvasService: CanvasService,
              private imageGenerateService: ImageGenerateService,
              private commonService: CommonService,
              private signDocumentService: SignDocumentService) {
  }

  sanitizeUrl(url: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  /** base64 thô -> data URL (nếu đã có prefix thì giữ nguyên) */
  toDataUrl(base64: string): string {
    if (!base64) {
      return null;
    }
    return base64.indexOf('base64,') >= 0 ? base64 : 'data:image/png;base64,' + base64;
  }

  @Input() defaultSignImageType: DefaultSignImageType = null;
  // JSON cấu hình hiển thị ảnh hệ thống đã lưu (chuỗi, null nếu chưa có)
  @Input() savedSignConfigJson: string = null;
  // ảnh hệ thống dùng tên chủ thể của chứng thư
  @Input() issuedTo: string = null;
  @Input() typeCertificate: number = null;
  // ảnh upload đã lưu (base64) và ảnh vẽ đã lưu (base64, khôi phục vào ô vẽ)
  @Input() savedImageBase64: string = null;
  @Input() savedDrawImageBase64: string = null;
  // khóa toàn block (dùng khi doanh nghiệp bị khóa)
  @Input() isDisabled = false;

  @Output() typeChange = new EventEmitter<DefaultSignImageType>();
  @Output() signConfigChange = new EventEmitter<string>();
  @Output() uploadImageChange = new EventEmitter<string>();   // base64 dataURL
  @Output() drawImageChange = new EventEmitter<string>();     // base64 dataURL
  // ảnh ký nhúng trong ảnh hệ thống (raw base64, đã strip prefix).
  // null = không có ảnh mới (giữ nguyên / tắt tích) — BE hiểu null là giữ path cũ trong config
  @Output() signatureImageChange = new EventEmitter<string>();

  /** node render fallback html-to-image cho image-sign-config */
  // @ts-ignore
  @ViewChild('previewContainer', {static: true}) previewContainer: ElementRef<HTMLDivElement>;

  // enum dung trong template/TS — dat enum thuong (const enum khong truyen duoc qua AOT webpack 8)
  DefaultSignImageType = DefaultSignImageType;
  enumSignMethod = EnumSignMethod;

  activeTab = 0;
  loading = false;

  // state từng tab (giữ khi chuyển qua lại)
  signConfig: SignatureConfig = SignatureConfig.build();
  uploadImageBase64: string = null;
  uploadImageName: string = null;
  // ảnh hệ thống đã gen (dataURL) — hiển thị ở tab 1 khi chưa có cấu hình lưu (PYC-84222)
  generatedSystemImage: string = null;
  generatingSystemImage = false;
  // lần đầu chưa có cấu hình lưu -> áp default đúng 1 lần; sau đó tôn trọng tick người dùng
  private appliedDefaultConfig = false;
  // ảnh nhúng theo config đã lưu (raw base64) — mốc so sánh để chỉ emit ảnh khi thực sự đổi
  private savedSignatureImageBase64: string = null;
  // seq chống race: response của request gen cũ không ghi đè ảnh mới
  private genSeq = 0;

  wrongFileFormat = false;
  fileTooLarge = false;

  /** selectedCert tối giản để image-sign-config render (chỉ dùng typeCertificate) */
  get selectedCertProxy() {
    return {
      typeCertificate: this.typeCertificate,
      issuedTo: this.issuedTo,
      id: null,
      idDetail: null,
      serialNumber: null,
      phoneNumber: null,
      imageUrl: null,
      taxCode: null
    };
  }

  /** node render fallback html-to-image cho image-sign-config */
  get domRenderImageProxy(): HTMLDivElement {
    return this.previewContainer ? this.previewContainer.nativeElement : null;
  }

  ngOnChanges(changes: SimpleChanges) {
    // nạp dữ liệu đã lưu 1 lần từ input (màn hình load xong mới có)
    if (changes.defaultSignImageType && this.defaultSignImageType != null) {
      this.activeTab = this.defaultSignImageType;
    } else if (changes.defaultSignImageType && changes.defaultSignImageType.currentValue === null
      && changes.defaultSignImageType.firstChange) {
      this.activeTab = DefaultSignImageType.SYSTEM;
    }
    if (changes.savedSignConfigJson && this.savedSignConfigJson) {
      this.restoreSignConfig(this.savedSignConfigJson);
    }
    if (changes.savedImageBase64 && this.savedImageBase64) {
      // API trả base64 thô (không có prefix) — bọc data URL, nếu không browser coi là URL tương đối -> 404
      this.uploadImageBase64 = this.toDataUrl(this.savedImageBase64);
    }
    if ((changes.issuedTo || changes.savedSignConfigJson) && this.signConfig && this.issuedTo) {
      // tên chủ thể luôn lấy theo chứng thư hiện tại
      this.signConfig.enterpriseName.value = this.issuedTo;
      this.signConfig.confirmBy.value = this.issuedTo;
    }
    // loại hệ thống (0) hoặc chưa cấu hình (null) -> gen ảnh async, không block UI
    if (this.defaultSignImageType == null || this.defaultSignImageType === DefaultSignImageType.SYSTEM) {
      if (changes.issuedTo) {
        // đổi chủ thể -> gen lại cho đúng tên
        this.generatedSystemImage = null;
      }
      if (this.issuedTo) {
        this.generateSystemImage();
      }
    }
  }

  /**
   * Gọi genimage tạo ảnh hệ thống (PYC-84222): đã có defaultSignConfig thì signConfig
   * đã restore từ BE; chưa cấu hình -> mặc định tên chủ thể + dấu tích xanh.
   * Config chỉnh THẲNG trên this.signConfig để preview trong dialog Edit khớp với ảnh gen.
   * Bất đồng bộ, xong thì tự hiện vào khung preview; không loading block UI.
   */
  async generateSystemImage() {
    if (this.generatingSystemImage) {
      return;
    }
    const seq = ++this.genSeq;
    this.generatingSystemImage = true;
    if (!this.savedSignConfigJson && !this.appliedDefaultConfig) {
      // SignatureConfig.build() mặc định bật greenTick nhưng chưa có tên chủ thể:
      // áp default đúng LẦN ĐẦU (tên + ngày giờ ký hiện tại + Roboto);
      // các lần gen sau tôn trọng tick hiện tại của người dùng
      this.appliedDefaultConfig = true;
      this.signConfig.enterpriseName.enabled = true;
      this.signConfig.enterpriseName.value = this.issuedTo;
      this.signConfig.greenTick.enabled = true;
      this.signConfig.fontFamily.value = 'Roboto';
      const datePipe = new DatePipe('vi-VN');
      this.signConfig.signingTime.enabled = true;
      this.signConfig.signingTime.value = datePipe.transform(new Date(), 'HH:mm dd/MM/yyyy');
    }
    // ngày giờ ký bật -> luôn lấy giờ hiện tại (ảnh preview phải là giờ mới nhất)
    if (this.signConfig.signingTime.enabled) {
      const datePipe = new DatePipe('vi-VN');
      this.signConfig.signingTime.value = datePipe.transform(new Date(), 'HH:mm dd/MM/yyyy');
    }
    // email bật -> lấy email người ký từ get-email-user (như màn ký); JSON config
    // không lưu giá trị email nên không fill thì dòng Email trong ảnh bị trống
    if (this.signConfig.email && this.signConfig.email.enabled) {
      this.signConfig.email.value = await this.ensureEmailValue();
    }
    // request cỡ 300x136 như màn ký duyệt: font trong genimage cỡ cố định,
    // request nhỏ -> chữ to tương đối; ảnh sẽ được kéo giãn 4x vào khung hiển thị
    this.imageGenerateService.genImage(this.signConfig, {width: '300px', height: '136px'})
      .subscribe(rs => {
        if (seq !== this.genSeq) {
          return;  // request cũ, đã có request mới -> bỏ qua
        }
        const file = new File([rs], 'system-image.png', {type: 'image/png'});
        this.commonService.getBase64(file).then(r => {
          if (seq !== this.genSeq) {
            return;
          }
          this.generatedSystemImage = r as string;
          this.generatingSystemImage = false;
        });
      }, () => {
        if (seq !== this.genSeq) {
          return;
        }
        // lỗi gen image: im lặng, tab hệ thống giữ cấu hình form như cũ
        this.generatingSystemImage = false;
      });
  }

  /** email người ký từ get-email-user (như màn ký); cache 1 lần, lỗi -> '' */
  private emailValue: string = null;

  private async ensureEmailValue(): Promise<string> {
    if (this.emailValue !== null) {
      return this.emailValue;
    }
    try {
      const res = await this.signDocumentService.getEmailAsync().toPromise();
      this.emailValue = res && res.success ? (res.data || '') : '';
    } catch (e) {
      this.emailValue = '';
    }
    return this.emailValue;
  }

  private restoreSignConfig(json: string) {
    try {
      const saved = JSON.parse(json);
      this.savedSignatureImageBase64 = null;
      if (saved.signatureImageBase64 && saved.signatureImageBase64.value) {
        // BE trả ảnh nhúng dạng data URL nhúng sẵn trong config — ghi mốc so sánh
        this.savedSignatureImageBase64 = this.toRawBase64(saved.signatureImageBase64.value);
      }
      const config = SignatureConfig.build();
      config.fontFamily.value = 'Roboto';
      for (const key in config) {
        if (saved[key] && typeof saved[key] === 'object') {
          config[key].enabled = !!saved[key].enabled;
          if (saved[key].value !== undefined && saved[key].value !== null && saved[key].value !== '') {
            config[key].value = saved[key].value;
          }
        } else if (saved[key] !== undefined && key === 'contentPosition') {
          config[key].value = saved[key];
        }
      }
      // font mặc định Roboto khi config lưu chưa có font
      if (!saved.fontFamily || !saved.fontFamily.value) {
        config.fontFamily.value = 'Roboto';
      }
      // tên chủ thể luôn lấy theo chứng thư hiện tại
      if (this.issuedTo) {
        config.enterpriseName.value = this.issuedTo;
        config.confirmBy.value = this.issuedTo;
      }
      this.signConfig = config;
    } catch (e) {
      this.signConfig = SignatureConfig.build();
      this.signConfig.fontFamily.value = 'Roboto';
    }
  }

  onTabChange(index: number) {
    this.activeTab = index;
    this.typeChange.emit(index);
  }

  onSignConfigChange() {
    this.signConfigChange.emit(this.buildCompactSignConfig());
    this.emitSignatureImageChange();
    // config đổi (bấm Xong trong dialog Edit) -> gen lại ảnh hệ thống cho khớp cấu hình mới
    this.generatedSystemImage = null;
    this.generateSystemImage();
  }

  /**
   * Ảnh ký nhúng chỉ gửi BE khi bật tích VÀ ảnh thực sự khác lần load
   * (tránh gửi lại base64 cũ mỗi lần lưu — BE tự giữ path cũ trong config).
   * Tắt tích / giữ nguyên ảnh -> emit null, BE giữ nguyên signatureImagePath.
   */
  private emitSignatureImageChange() {
    const imageItem = this.signConfig.signatureImageBase64;
    const imageEnabled = this.signConfig.signatureImage && this.signConfig.signatureImage.enabled;
    if (!imageEnabled || !imageItem || !imageItem.value) {
      this.signatureImageChange.emit(null);
      return;
    }
    const raw = this.toRawBase64(imageItem.value);
    if (raw && raw === this.savedSignatureImageBase64) {
      this.signatureImageChange.emit(null);
      return;
    }
    this.signatureImageChange.emit(raw);
  }

  /** data URL (hoặc base64 thô) -> base64 thô */
  private toRawBase64(value: string): string {
    if (!value) {
      return null;
    }
    const marker = 'base64,';
    return value.indexOf(marker) >= 0 ? value.substring(value.indexOf(marker) + marker.length) : value;
  }

  /**
   * Serialize config rút gọn: chỉ field đang bật + các field có giá trị thực dụng
   * (font/contentPosition/ratio...), bỏ field tắt & null để payload gọn gửi BE.
   */
  private buildCompactSignConfig(): string {
    const compact: any = {};
    for (const key in this.signConfig) {
      const item = this.signConfig[key];
      if (!item) {
        continue;
      }
      // base64 ảnh nhúng KHÔNG BAO GIỜ nằm trong JSON config — gửi riêng qua
      // signatureImageChange cho BE upload rồi ghi path vào JSON (mirror save-user-config).
      // Không skip thì nhánh item.enabled dưới đây bắt trúng giá trị BE-inject
      // (enabled: true) và nhét nguyên chuỗi dataURL vào defaultSignConfig
      if (key === 'signatureImageBase64') {
        continue;
      }
      const hasValue = item.value !== null && item.value !== undefined && item.value !== '';
      if (item.enabled) {
        compact[key] = {
          enabled: true,
          // email là giá trị theo người ký tại thời điểm ký (fill từ get-email-user
          // lúc gen/ký) — không lưu value vào defaultSignConfig, tránh lúc ký
          // applyCertDefaultSignConfig đè email của người ký thật
          value: key === 'email' ? null : (hasValue ? item.value : null)
        };
      } else if (key === 'fontFamily' || key === 'signatureRatio' || key === 'greenTick'
        || key === 'signatureImage' || key === 'signaturePosition' || key === 'signatureImageName'
        || key === 'isDraw') {
        // giá trị mặc định ảnh hưởng render, giữ lại dù không bật
        // (greenTick phải luôn có mặt: nếu drop thì lần restore sau sẽ rơi vào
        // default enabled=true của SignatureConfig.build() -> dấu tích tự bật lại)
        // (signatureImage/signaturePosition/signatureImageName/isDraw là value-carrier
        // của ảnh ký nhúng — base64 KHÔNG nằm trong JSON này, gửi riêng qua signatureImageChange)
        compact[key] = {enabled: false, value: item.value};
      }
    }
    return JSON.stringify(compact);
  }

  /** nz-upload không tự upload — giữ file để đọc base64 */
  dummyRequest = (item: any) => {
    const file: File = item.file;
    const ext = (file.type || '').split('/')[1];
    this.wrongFileFormat = !(ext === 'png' || ext === 'jpg' || ext === 'jpeg');
    this.fileTooLarge = !this.wrongFileFormat && (file.size / 1024 / 1024 > 2);
    if (this.wrongFileFormat || this.fileTooLarge) {
      return;
    }
    const reader = new FileReader();
    reader.onload = (event: any) => {
      this.uploadImageBase64 = event.target.result;
      this.uploadImageName = file.name;
      this.uploadImageChange.emit(this.uploadImageBase64);
    };
    reader.readAsDataURL(file);
    setTimeout(() => {
      item.onSuccess('ok');
    }, 0);
  }

  beforeUpload = (file: NzUploadFile, _fileList: NzUploadFile[]): Observable<boolean> => {
    return new Observable((observer: Observer<boolean>) => {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        this.wrongFileFormat = true;
        this.fileTooLarge = false;
        observer.complete();
        return;
      }
      const isLt2M = file.size! / 1024 / 1024 < 2;
      if (!isLt2M) {
        this.wrongFileFormat = false;
        this.fileTooLarge = true;
        observer.complete();
        return;
      }
      observer.next(true);
      observer.complete();
    });
  }

  deleteUploadImage() {
    this.uploadImageBase64 = null;
    this.uploadImageName = null;
    this.uploadImageChange.emit(null);
  }

  applyDrawImage(data: string) {
    // data = null khi user bấm "Vẽ lại" (forward từ canvasCleared) — cha hiểu null là reset
    this.drawImageChange.emit(data);
  }
}
